import { computed, unref } from 'vue';
import { fitMapToGeoJsonBounds } from '@/utils/mapGeometry';

export function resolveVMapInstance(mapRef) {
    const candidate = unref(mapRef);
    return candidate?.map ?? null;
}

function normalizeLatLng(value) {
    if (!value) {
        return null;
    }

    if (Array.isArray(value) && value.length >= 2) {
        const lat = Number(value[0]);
        const lng = Number(value[1]);
        return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
    }

    const lat = Number(value.lat);
    const lng = Number(value.lng ?? value.lon);
    return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
}

export function useVMapInstance(mapRef) {
    const map = computed(() => resolveVMapInstance(mapRef));
    const isReady = computed(() => Boolean(map.value));

    const getMap = () => map.value;

    const getZoom = (fallback = null) => {
        const zoom = Number(getMap()?.getZoom?.());
        return Number.isFinite(zoom) ? zoom : fallback;
    };

    const invalidateSize = (options) => {
        const mapInstance = getMap();
        if (typeof mapInstance?.invalidateSize !== 'function') {
            return false;
        }

        mapInstance.invalidateSize(options);
        return true;
    };

    const fitToGeoJsonBounds = (entries, options = {}) => {
        return fitMapToGeoJsonBounds(getMap(), entries, options);
    };

    const flyToPoint = (latLng, zoom = null, options = {}) => {
        const mapInstance = getMap();
        const point = normalizeLatLng(latLng);
        if (!mapInstance || !point) {
            return false;
        }

        if (typeof mapInstance.flyTo === 'function') {
            mapInstance.flyTo(point, zoom ?? getZoom(), options);
            return true;
        }

        if (typeof mapInstance.setView === 'function') {
            mapInstance.setView(point, zoom ?? getZoom(), options);
            return true;
        }

        if (typeof mapInstance.panTo === 'function') {
            mapInstance.panTo(point, options);
            return true;
        }

        return false;
    };

    const onMapEvent = (eventName, handler) => {
        const mapInstance = getMap();
        if (!eventName || typeof handler !== 'function' || typeof mapInstance?.on !== 'function') {
            return () => { };
        }

        mapInstance.on(eventName, handler);

        return () => {
            mapInstance.off?.(eventName, handler);
        };
    };

    return {
        map,
        isReady,
        getMap,
        getZoom,
        invalidateSize,
        fitToGeoJsonBounds,
        flyToPoint,
        onMapEvent,
    };
}
